/**
 * The grape varieties of each wine region (owner request, 2026-10-08): the Cépage list shows only
 * the varieties of the wine's region; any other is found with the search or added. "Autre région"
 * (or no region yet) shows every variety of the country; no country shows them all. Names are
 * spelled as in domain/grapes.ts (the French name, or the variety's own where it is mostly known
 * by it), so "Shiraz" is Syrah and "Garnacha" Grenache.
 */
import { REGION_OTHER } from './regions';

const BORDEAUX_REDS = ['Cabernet-Sauvignon', 'Merlot', 'Cabernet franc', 'Petit verdot', 'Malbec'];
const ENGLISH = [
  'Chardonnay',
  'Pinot noir',
  'Meunier',
  'Bacchus',
  'Seyval blanc',
  'Pinot gris',
  'Pinot blanc',
  'Solaris',
  'Ortega',
  'Reichensteiner',
  'Rondo',
  'Regent',
];
const LOW_COUNTRIES = [
  'Solaris',
  'Johanniter',
  'Souvignier gris',
  'Regent',
  'Rondo',
  'Pinot noir',
  'Pinot gris',
  'Pinot blanc',
  'Auxerrois',
  'Chardonnay',
  'Müller-Thurgau',
];
const BRITISH_COLUMBIA = [
  'Merlot',
  'Pinot noir',
  'Chardonnay',
  'Pinot gris',
  'Cabernet franc',
  'Syrah',
  'Cabernet-Sauvignon',
  'Riesling',
  'Gewurztraminer',
  'Sauvignon blanc',
  'Gamay',
  'Viognier',
];
const MOROCCO = [
  'Cinsault',
  'Carignan',
  'Grenache',
  'Syrah',
  'Cabernet-Sauvignon',
  'Merlot',
  'Mourvèdre',
  'Marselan',
  'Clairette',
  'Muscat d’Alexandrie',
  'Chardonnay',
  'Sauvignon blanc',
];
const ISRAEL = [
  'Cabernet-Sauvignon',
  'Merlot',
  'Syrah',
  'Carignan',
  'Petite Sirah',
  'Marselan',
  'Cabernet franc',
  'Chardonnay',
  'Sauvignon blanc',
  'Grenache',
  'Viognier',
];
const LEBANON = [
  'Cinsault',
  'Cabernet-Sauvignon',
  'Syrah',
  'Carignan',
  'Grenache',
  'Merlot',
  'Mourvèdre',
  'Obaideh',
  'Merwah',
  'Chardonnay',
  'Viognier',
  'Sauvignon blanc',
];
const CHINA = [
  'Cabernet-Sauvignon',
  'Merlot',
  'Marselan',
  'Cabernet franc',
  'Carmenère',
  'Syrah',
  'Petit verdot',
  'Chardonnay',
  'Riesling',
];
const URUGUAY = [
  'Tannat',
  'Albariño',
  'Merlot',
  'Cabernet franc',
  'Cabernet-Sauvignon',
  'Marselan',
  'Pinot noir',
  'Sauvignon blanc',
  'Chardonnay',
  'Viognier',
];
const MEXICO = [
  'Cabernet-Sauvignon',
  'Nebbiolo',
  'Tempranillo',
  'Grenache',
  'Merlot',
  'Syrah',
  'Petite Sirah',
  'Zinfandel',
  'Malbec',
  'Chenin',
  'Sauvignon blanc',
  'Chardonnay',
];
const MOLDOVA = [
  'Feteasca neagra',
  'Rara neagra',
  'Feteasca alba',
  'Feteasca regala',
  'Cabernet-Sauvignon',
  'Merlot',
  'Saperavi',
  'Pinot noir',
  'Chardonnay',
  'Sauvignon blanc',
  'Muscat ottonel',
  'Rkatsiteli',
];

export const REGION_GRAPES: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> =
  {
    FR: {
      Alsace: [
        'Riesling',
        'Gewurztraminer',
        'Pinot gris',
        'Pinot blanc',
        'Auxerrois',
        'Sylvaner',
        'Muscat à petits grains',
        'Muscat ottonel',
        'Pinot noir',
        'Chasselas',
        'Klevener de Heiligenstein',
        'Chardonnay',
      ],
      Beaujolais: ['Gamay', 'Chardonnay', 'Aligoté', 'Pinot noir'],
      Bordeaux: [
        ...BORDEAUX_REDS,
        'Carmenère',
        'Sémillon',
        'Sauvignon blanc',
        'Sauvignon gris',
        'Muscadelle',
        'Colombard',
        'Ugni blanc',
      ],
      Bourgogne: [
        'Pinot noir',
        'Chardonnay',
        'Aligoté',
        'Gamay',
        'César',
        'Sauvignon blanc',
        'Pinot blanc',
        'Pinot gris',
      ],
      Champagne: [
        'Chardonnay',
        'Pinot noir',
        'Meunier',
        'Pinot blanc',
        'Pinot gris',
        'Arbane',
        'Petit meslier',
      ],
      Corse: [
        'Nielluccio',
        'Sciaccarellu',
        'Vermentino',
        'Grenache',
        'Carignan',
        'Cinsault',
        'Syrah',
        'Bianco gentile',
        'Muscat à petits grains',
      ],
      Jura: ['Savagnin', 'Chardonnay', 'Poulsard', 'Trousseau', 'Pinot noir'],
      Languedoc: [
        'Grenache',
        'Syrah',
        'Mourvèdre',
        'Carignan',
        'Cinsault',
        'Lledoner pelut',
        'Grenache blanc',
        'Grenache gris',
        'Roussanne',
        'Marsanne',
        'Rolle',
        'Picpoul',
        'Bourboulenc',
        'Clairette',
        'Macabeu',
        'Terret',
        'Aramon',
        'Muscat à petits grains',
        'Mauzac',
        'Chardonnay',
        'Chenin',
        'Viognier',
        'Marselan',
        'Cabernet-Sauvignon',
        'Merlot',
      ],
      Loire: [
        'Chenin',
        'Sauvignon blanc',
        'Cabernet franc',
        'Melon de Bourgogne',
        'Gamay',
        'Pinot noir',
        'Grolleau',
        'Pinot d’Aunis',
        'Côt',
        'Cabernet-Sauvignon',
        'Chardonnay',
        'Romorantin',
        'Folle blanche',
        'Gros plant',
        'Sauvignon gris',
        'Chasselas',
        'Meunier',
      ],
      Provence: [
        'Grenache',
        'Cinsault',
        'Syrah',
        'Mourvèdre',
        'Tibouren',
        'Carignan',
        'Cabernet-Sauvignon',
        'Rolle',
        'Ugni blanc',
        'Clairette',
        'Sémillon',
        'Bourboulenc',
      ],
      Roussillon: [
        'Grenache',
        'Grenache gris',
        'Grenache blanc',
        'Carignan',
        'Syrah',
        'Mourvèdre',
        'Lledoner pelut',
        'Macabeu',
        'Muscat à petits grains',
        'Muscat d’Alexandrie',
        'Rolle',
        'Roussanne',
        'Marsanne',
      ],
      Savoie: [
        'Jacquère',
        'Altesse',
        'Mondeuse',
        'Gamay',
        'Pinot noir',
        'Chasselas',
        'Roussanne',
        'Gringet',
        'Persan',
        'Molette',
        'Verdesse',
        'Chardonnay',
      ],
      'Sud-Ouest': [
        'Tannat',
        'Côt',
        'Malbec',
        'Négrette',
        'Fer servadou',
        'Duras',
        'Abouriou',
        'Prunelard',
        'Cabernet franc',
        'Cabernet-Sauvignon',
        'Merlot',
        'Gros manseng',
        'Petit manseng',
        'Courbu',
        'Petit courbu',
        'Arrufiac',
        'Camaralet',
        'Len de l’el',
        'Mauzac',
        'Ondenc',
        'Sauvignon blanc',
        'Sémillon',
        'Muscadelle',
        'Colombard',
        'Ugni blanc',
      ],
      'Vallée du Rhône': [
        'Syrah',
        'Grenache',
        'Mourvèdre',
        'Cinsault',
        'Carignan',
        'Counoise',
        'Vaccarèse',
        'Terret',
        'Viognier',
        'Marsanne',
        'Roussanne',
        'Grenache blanc',
        'Clairette',
        'Bourboulenc',
        'Picpoul',
        'Ugni blanc',
        'Muscat à petits grains',
      ],
    },
    IT: {
      Abruzzo: ['Montepulciano', 'Trebbiano', 'Pecorino', 'Passerina', 'Cococciola'],
      'Alto Adige': [
        'Lagrein',
        'Schiava',
        'Pinot noir',
        'Gewurztraminer',
        'Pinot blanc',
        'Pinot gris',
        'Chardonnay',
        'Sauvignon blanc',
        'Kerner',
        'Müller-Thurgau',
        'Sylvaner',
        'Riesling',
        'Merlot',
        'Cabernet-Sauvignon',
        'Muscat à petits grains',
      ],
      Campania: ['Aglianico', 'Piedirosso', 'Fiano', 'Greco', 'Falanghina', 'Coda di volpe'],
      'Emilia-Romagna': [
        'Lambrusco',
        'Sangiovese',
        'Barbera',
        'Bonarda',
        'Albana',
        'Pignoletto',
        'Trebbiano',
        'Malvasia',
      ],
      'Friuli-Venezia Giulia': [
        'Friulano',
        'Ribolla gialla',
        'Pinot gris',
        'Sauvignon blanc',
        'Malvasia',
        'Chardonnay',
        'Picolit',
        'Verduzzo',
        'Refosco',
        'Schioppettino',
        'Merlot',
        'Cabernet franc',
      ],
      Lombardia: [
        'Nebbiolo',
        'Chardonnay',
        'Pinot noir',
        'Pinot blanc',
        'Barbera',
        'Bonarda',
        'Groppello',
        'Trebbiano',
        'Merlot',
      ],
      Marche: ['Verdicchio', 'Pecorino', 'Passerina', 'Montepulciano', 'Sangiovese', 'Lacrima'],
      Piemonte: [
        'Nebbiolo',
        'Barbera',
        'Dolcetto',
        'Freisa',
        'Grignolino',
        'Ruché',
        'Pelaverga',
        'Brachetto',
        'Arneis',
        'Cortese',
        'Timorasso',
        'Erbaluce',
        'Muscat à petits grains',
        'Chardonnay',
        'Pinot noir',
      ],
      Puglia: [
        'Primitivo',
        'Negroamaro',
        'Nero di Troia',
        'Susumaniello',
        'Malvasia',
        'Bombino',
        'Fiano',
        'Chardonnay',
      ],
      Sardegna: [
        'Cannonau',
        'Carignan',
        'Monica',
        'Bovale',
        'Cagnulari',
        'Vermentino',
        'Nuragus',
        'Vernaccia',
        'Malvasia',
        'Muscat à petits grains',
      ],
      Sicilia: [
        'Nero d’Avola',
        'Nerello mascalese',
        'Nerello cappuccio',
        'Frappato',
        'Perricone',
        'Grillo',
        'Carricante',
        'Catarratto',
        'Insolia',
        'Muscat d’Alexandrie',
        'Malvasia',
        'Syrah',
        'Chardonnay',
      ],
      Toscana: [
        'Sangiovese',
        'Canaiolo',
        'Colorino',
        'Ciliegiolo',
        'Cabernet-Sauvignon',
        'Merlot',
        'Cabernet franc',
        'Petit verdot',
        'Syrah',
        'Vernaccia',
        'Vermentino',
        'Trebbiano',
        'Malvasia',
      ],
      Trentino: [
        'Teroldego',
        'Marzemino',
        'Lagrein',
        'Schiava',
        'Pinot noir',
        'Merlot',
        'Cabernet-Sauvignon',
        'Chardonnay',
        'Pinot gris',
        'Müller-Thurgau',
        'Nosiola',
        'Gewurztraminer',
      ],
      Umbria: [
        'Sagrantino',
        'Sangiovese',
        'Merlot',
        'Cabernet-Sauvignon',
        'Grechetto',
        'Trebbiano',
        'Malvasia',
      ],
      Veneto: [
        'Corvina',
        'Corvinone',
        'Rondinella',
        'Molinara',
        'Raboso',
        'Merlot',
        'Cabernet franc',
        'Cabernet-Sauvignon',
        'Garganega',
        'Glera',
        'Durella',
        'Trebbiano',
        'Pinot gris',
        'Chardonnay',
      ],
    },
    ES: {
      Bierzo: ['Mencía', 'Alicante Bouschet', 'Godello', 'Doña Blanca', 'Palomino'],
      Cava: [
        'Macabeu',
        'Xarel·lo',
        'Parellada',
        'Chardonnay',
        'Malvasia',
        'Pinot noir',
        'Grenache',
        'Monastrell',
        'Trepat',
      ],
      Jerez: ['Palomino', 'Pedro Ximénez', 'Muscat d’Alexandrie'],
      Jumilla: [
        'Monastrell',
        'Tempranillo',
        'Grenache',
        'Alicante Bouschet',
        'Syrah',
        'Cabernet-Sauvignon',
        'Merlot',
        'Petit verdot',
        'Airén',
        'Macabeu',
        'Malvasia',
      ],
      'La Mancha': [
        'Airén',
        'Tempranillo',
        'Grenache',
        'Bobal',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
        'Petit verdot',
        'Macabeu',
        'Verdejo',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      Navarra: [
        'Grenache',
        'Tempranillo',
        'Graciano',
        'Carignan',
        'Cabernet-Sauvignon',
        'Merlot',
        'Macabeu',
        'Grenache blanc',
        'Chardonnay',
        'Muscat à petits grains',
      ],
      Penedès: [
        'Xarel·lo',
        'Macabeu',
        'Parellada',
        'Chardonnay',
        'Muscat à petits grains',
        'Tempranillo',
        'Grenache',
        'Monastrell',
        'Sumoll',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
      ],
      Priorat: [
        'Grenache',
        'Carignan',
        'Syrah',
        'Cabernet-Sauvignon',
        'Merlot',
        'Grenache blanc',
        'Macabeu',
        'Pedro Ximénez',
      ],
      'Rías Baixas': ['Albariño', 'Loureiro', 'Treixadura', 'Caíño', 'Godello', 'Mencía'],
      'Ribera del Duero': [
        'Tempranillo',
        'Cabernet-Sauvignon',
        'Merlot',
        'Malbec',
        'Grenache',
        'Albillo',
      ],
      Rioja: [
        'Tempranillo',
        'Grenache',
        'Graciano',
        'Carignan',
        'Maturana',
        'Macabeu',
        'Malvasia',
        'Grenache blanc',
        'Tempranillo blanco',
        'Verdejo',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      Rueda: [
        'Verdejo',
        'Sauvignon blanc',
        'Macabeu',
        'Palomino',
        'Viognier',
        'Chardonnay',
        'Tempranillo',
      ],
      Toro: ['Tempranillo', 'Grenache', 'Malvasia', 'Verdejo'],
    },
    PT: {
      Alentejo: [
        'Trincadeira',
        'Alicante Bouschet',
        'Tinta roriz',
        'Castelão',
        'Touriga nacional',
        'Syrah',
        'Cabernet-Sauvignon',
        'Antão Vaz',
        'Arinto',
        'Roupeiro',
        'Verdelho',
      ],
      Bairrada: [
        'Baga',
        'Touriga nacional',
        'Cabernet-Sauvignon',
        'Merlot',
        'Bical',
        'Maria Gomes',
        'Arinto',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      Dão: [
        'Touriga nacional',
        'Tinta roriz',
        'Jaen',
        'Alfrocheiro',
        'Encruzado',
        'Malvasia',
        'Bical',
      ],
      Douro: [
        'Touriga nacional',
        'Touriga franca',
        'Tinta roriz',
        'Tinta barroca',
        'Tinto cão',
        'Sousão',
        'Rabigato',
        'Viosinho',
        'Gouveio',
        'Códega do Larinho',
        'Malvasia',
        'Arinto',
      ],
      Lisboa: [
        'Castelão',
        'Touriga nacional',
        'Tinta roriz',
        'Syrah',
        'Alicante Bouschet',
        'Cabernet-Sauvignon',
        'Arinto',
        'Fernão Pires',
        'Vital',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      Madeira: ['Sercial', 'Verdelho', 'Boal', 'Malvasia', 'Terrantez', 'Tinta negra'],
      Porto: [
        'Touriga nacional',
        'Touriga franca',
        'Tinta roriz',
        'Tinta barroca',
        'Tinto cão',
        'Sousão',
        'Malvasia',
        'Gouveio',
        'Viosinho',
        'Rabigato',
      ],
      Setúbal: [
        'Castelão',
        'Touriga nacional',
        'Syrah',
        'Alicante Bouschet',
        'Cabernet-Sauvignon',
        'Muscat d’Alexandrie',
        'Moscatel roxo',
        'Arinto',
        'Fernão Pires',
      ],
      'Vinho Verde': [
        'Albariño',
        'Loureiro',
        'Arinto',
        'Avesso',
        'Treixadura',
        'Azal',
        'Vinhão',
        'Espadeiro',
      ],
    },
    DE: {
      Ahr: ['Pinot noir', 'Frühburgunder', 'Portugieser', 'Dornfelder', 'Riesling'],
      Baden: [
        'Pinot noir',
        'Pinot gris',
        'Pinot blanc',
        'Müller-Thurgau',
        'Chasselas',
        'Riesling',
        'Sylvaner',
        'Gewurztraminer',
        'Auxerrois',
        'Chardonnay',
      ],
      Franken: [
        'Sylvaner',
        'Müller-Thurgau',
        'Bacchus',
        'Riesling',
        'Kerner',
        'Scheurebe',
        'Pinot blanc',
        'Pinot noir',
        'Domina',
      ],
      Mosel: [
        'Riesling',
        'Müller-Thurgau',
        'Elbling',
        'Kerner',
        'Pinot blanc',
        'Auxerrois',
        'Pinot noir',
        'Dornfelder',
      ],
      Nahe: [
        'Riesling',
        'Müller-Thurgau',
        'Sylvaner',
        'Pinot gris',
        'Pinot blanc',
        'Scheurebe',
        'Kerner',
        'Pinot noir',
        'Dornfelder',
      ],
      Pfalz: [
        'Riesling',
        'Müller-Thurgau',
        'Pinot gris',
        'Pinot blanc',
        'Scheurebe',
        'Sylvaner',
        'Kerner',
        'Gewurztraminer',
        'Muscat à petits grains',
        'Sauvignon blanc',
        'Chardonnay',
        'Pinot noir',
        'Dornfelder',
        'Portugieser',
      ],
      Rheingau: ['Riesling', 'Pinot noir', 'Müller-Thurgau', 'Pinot blanc'],
      Rheinhessen: [
        'Riesling',
        'Müller-Thurgau',
        'Sylvaner',
        'Pinot gris',
        'Pinot blanc',
        'Kerner',
        'Scheurebe',
        'Bacchus',
        'Chardonnay',
        'Sauvignon blanc',
        'Dornfelder',
        'Pinot noir',
        'Portugieser',
      ],
      Württemberg: [
        'Trollinger',
        'Blaufränkisch',
        'Meunier',
        'Pinot noir',
        'Portugieser',
        'Dornfelder',
        'Sankt Laurent',
        'Riesling',
        'Müller-Thurgau',
        'Kerner',
        'Sylvaner',
        'Pinot gris',
      ],
    },
    AT: {
      Burgenland: [
        'Blaufränkisch',
        'Zweigelt',
        'Sankt Laurent',
        'Pinot noir',
        'Cabernet-Sauvignon',
        'Merlot',
        'Welschriesling',
        'Chardonnay',
        'Pinot blanc',
        'Grüner Veltliner',
        'Neuburger',
        'Furmint',
        'Muscat ottonel',
      ],
      Kamptal: [
        'Grüner Veltliner',
        'Riesling',
        'Chardonnay',
        'Pinot blanc',
        'Sauvignon blanc',
        'Zweigelt',
        'Pinot noir',
      ],
      Kremstal: [
        'Grüner Veltliner',
        'Riesling',
        'Chardonnay',
        'Pinot blanc',
        'Sauvignon blanc',
        'Zweigelt',
        'Pinot noir',
      ],
      Steiermark: [
        'Sauvignon blanc',
        'Welschriesling',
        'Chardonnay',
        'Muscat à petits grains',
        'Pinot blanc',
        'Pinot gris',
        'Gewurztraminer',
        'Riesling',
        'Zweigelt',
        'Blauer Wildbacher',
      ],
      Wachau: [
        'Grüner Veltliner',
        'Riesling',
        'Neuburger',
        'Muscat à petits grains',
        'Pinot blanc',
        'Chardonnay',
        'Zweigelt',
      ],
      Weinviertel: [
        'Grüner Veltliner',
        'Riesling',
        'Welschriesling',
        'Pinot blanc',
        'Müller-Thurgau',
        'Sauvignon blanc',
        'Chardonnay',
        'Zweigelt',
        'Blaufränkisch',
        'Portugieser',
        'Pinot noir',
      ],
      Wien: [
        'Grüner Veltliner',
        'Riesling',
        'Pinot blanc',
        'Chardonnay',
        'Neuburger',
        'Welschriesling',
        'Muscat à petits grains',
        'Zweigelt',
        'Pinot noir',
      ],
    },
    CH: {
      Genève: [
        'Chasselas',
        'Gamay',
        'Pinot noir',
        'Gamaret',
        'Garanoir',
        'Merlot',
        'Chardonnay',
        'Aligoté',
        'Sauvignon blanc',
      ],
      Neuchâtel: ['Chasselas', 'Pinot noir', 'Chardonnay', 'Pinot gris'],
      Tessin: [
        'Merlot',
        'Bondola',
        'Cabernet franc',
        'Cabernet-Sauvignon',
        'Pinot noir',
        'Chardonnay',
      ],
      Valais: [
        'Chasselas',
        'Pinot noir',
        'Gamay',
        'Syrah',
        'Cornalin',
        'Humagne rouge',
        'Petite Arvine',
        'Amigne',
        'Savagnin',
        'Marsanne',
        'Sylvaner',
        'Pinot gris',
        'Muscat à petits grains',
      ],
      Vaud: [
        'Chasselas',
        'Pinot noir',
        'Gamay',
        'Gamaret',
        'Garanoir',
        'Merlot',
        'Chardonnay',
        'Pinot gris',
      ],
    },
    US: {
      'Central Coast': [
        'Chardonnay',
        'Pinot noir',
        'Syrah',
        'Grenache',
        'Cabernet-Sauvignon',
        'Merlot',
        'Zinfandel',
        'Sauvignon blanc',
        'Riesling',
        'Viognier',
      ],
      'Columbia Valley': [
        ...BORDEAUX_REDS,
        'Syrah',
        'Grenache',
        'Riesling',
        'Chardonnay',
        'Sauvignon blanc',
        'Pinot gris',
        'Sémillon',
        'Viognier',
      ],
      'Finger Lakes': [
        'Riesling',
        'Chardonnay',
        'Gewurztraminer',
        'Grüner Veltliner',
        'Pinot gris',
        'Rkatsiteli',
        'Vidal',
        'Seyval blanc',
        'Cabernet franc',
        'Pinot noir',
        'Blaufränkisch',
        'Saperavi',
        'Chambourcin',
      ],
      Lodi: [
        'Zinfandel',
        'Cabernet-Sauvignon',
        'Petite Sirah',
        'Merlot',
        'Syrah',
        'Tempranillo',
        'Carignan',
        'Cinsault',
        'Grenache',
        'Chardonnay',
        'Albariño',
        'Sauvignon blanc',
      ],
      Mendocino: [
        'Pinot noir',
        'Zinfandel',
        'Carignan',
        'Petite Sirah',
        'Cabernet-Sauvignon',
        'Chardonnay',
        'Gewurztraminer',
        'Riesling',
        'Sauvignon blanc',
      ],
      'Napa Valley': [
        ...BORDEAUX_REDS,
        'Zinfandel',
        'Pinot noir',
        'Petite Sirah',
        'Syrah',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      'Paso Robles': [
        'Cabernet-Sauvignon',
        'Syrah',
        'Grenache',
        'Mourvèdre',
        'Zinfandel',
        'Petite Sirah',
        'Merlot',
        'Tempranillo',
        'Roussanne',
        'Grenache blanc',
        'Viognier',
        'Chardonnay',
      ],
      'Santa Barbara': [
        'Pinot noir',
        'Chardonnay',
        'Syrah',
        'Grenache',
        'Mourvèdre',
        'Cabernet franc',
        'Sauvignon blanc',
        'Riesling',
        'Viognier',
        'Roussanne',
      ],
      Sonoma: [
        'Pinot noir',
        'Zinfandel',
        'Cabernet-Sauvignon',
        'Merlot',
        'Cabernet franc',
        'Syrah',
        'Petite Sirah',
        'Carignan',
        'Chardonnay',
        'Sauvignon blanc',
        'Gewurztraminer',
      ],
      'Willamette Valley': [
        'Pinot noir',
        'Pinot gris',
        'Chardonnay',
        'Riesling',
        'Pinot blanc',
        'Gewurztraminer',
        'Gamay',
      ],
    },
    AR: {
      'La Rioja': [
        'Torrontés',
        'Malbec',
        'Bonarda',
        'Cabernet-Sauvignon',
        'Syrah',
        'Muscat d’Alexandrie',
      ],
      Mendoza: [
        'Malbec',
        'Bonarda',
        'Cabernet-Sauvignon',
        'Cabernet franc',
        'Merlot',
        'Syrah',
        'Tempranillo',
        'Pinot noir',
        'Petit verdot',
        'Chardonnay',
        'Sauvignon blanc',
        'Sémillon',
        'Torrontés',
      ],
      Patagonia: [
        'Pinot noir',
        'Malbec',
        'Merlot',
        'Cabernet franc',
        'Cabernet-Sauvignon',
        'Chardonnay',
        'Sauvignon blanc',
        'Sémillon',
        'Torrontés',
      ],
      Salta: [
        'Torrontés',
        'Malbec',
        'Cabernet-Sauvignon',
        'Cabernet franc',
        'Tannat',
        'Bonarda',
        'Syrah',
        'Merlot',
      ],
      'San Juan': [
        'Syrah',
        'Malbec',
        'Bonarda',
        'Cabernet-Sauvignon',
        'Torrontés',
        'Muscat d’Alexandrie',
        'Pedro Ximénez',
        'Chardonnay',
        'Viognier',
      ],
    },
    CL: {
      Aconcagua: [
        'Syrah',
        'Cabernet-Sauvignon',
        'Carmenère',
        'Merlot',
        'Pinot noir',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      'Bío-Bío': [
        'Pinot noir',
        'País',
        'Cinsault',
        'Chardonnay',
        'Riesling',
        'Sauvignon blanc',
        'Gewurztraminer',
        'Muscat d’Alexandrie',
      ],
      Casablanca: [
        'Sauvignon blanc',
        'Chardonnay',
        'Pinot noir',
        'Syrah',
        'Riesling',
        'Gewurztraminer',
        'Viognier',
      ],
      Colchagua: [
        'Carmenère',
        ...BORDEAUX_REDS,
        'Syrah',
        'Mourvèdre',
        'Grenache',
        'Carignan',
        'Chardonnay',
        'Sauvignon blanc',
        'Viognier',
      ],
      Itata: ['Cinsault', 'País', 'Carignan', 'Muscat d’Alexandrie', 'Sémillon', 'Chardonnay'],
      Maipo: [...BORDEAUX_REDS, 'Carmenère', 'Syrah', 'Chardonnay', 'Sauvignon blanc'],
      Maule: [
        'Carignan',
        'País',
        'Cinsault',
        'Cabernet-Sauvignon',
        'Merlot',
        'Carmenère',
        'Malbec',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      Rapel: [
        'Carmenère',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
        'Malbec',
        'Carignan',
        'Chardonnay',
        'Sauvignon blanc',
      ],
    },
    AU: {
      'Adelaide Hills': [
        'Sauvignon blanc',
        'Chardonnay',
        'Pinot gris',
        'Grüner Veltliner',
        'Riesling',
        'Pinot noir',
        'Syrah',
      ],
      'Barossa Valley': [
        'Syrah',
        'Grenache',
        'Mourvèdre',
        'Cabernet-Sauvignon',
        'Tempranillo',
        'Riesling',
        'Sémillon',
        'Chardonnay',
        'Viognier',
      ],
      'Clare Valley': [
        'Riesling',
        'Sémillon',
        'Chardonnay',
        'Syrah',
        'Cabernet-Sauvignon',
        'Grenache',
        'Malbec',
      ],
      Coonawarra: [
        'Cabernet-Sauvignon',
        'Syrah',
        'Merlot',
        'Cabernet franc',
        'Petit verdot',
        'Chardonnay',
        'Riesling',
      ],
      'Eden Valley': ['Riesling', 'Chardonnay', 'Viognier', 'Syrah', 'Cabernet-Sauvignon'],
      'Hunter Valley': [
        'Sémillon',
        'Chardonnay',
        'Verdelho',
        'Syrah',
        'Cabernet-Sauvignon',
        'Tempranillo',
      ],
      'Margaret River': [
        ...BORDEAUX_REDS,
        'Syrah',
        'Chardonnay',
        'Sauvignon blanc',
        'Sémillon',
        'Chenin',
      ],
      'McLaren Vale': [
        'Syrah',
        'Grenache',
        'Mourvèdre',
        'Cabernet-Sauvignon',
        'Tempranillo',
        'Sangiovese',
        'Nero d’Avola',
        'Fiano',
        'Vermentino',
        'Chardonnay',
        'Viognier',
      ],
      Tasmania: [
        'Pinot noir',
        'Chardonnay',
        'Meunier',
        'Sauvignon blanc',
        'Riesling',
        'Pinot gris',
        'Gewurztraminer',
      ],
      'Yarra Valley': [
        'Pinot noir',
        'Chardonnay',
        'Syrah',
        'Cabernet-Sauvignon',
        'Nebbiolo',
        'Meunier',
        'Sauvignon blanc',
        'Pinot gris',
      ],
    },
    NZ: {
      Canterbury: [
        'Pinot noir',
        'Riesling',
        'Sauvignon blanc',
        'Chardonnay',
        'Pinot gris',
        'Gewurztraminer',
      ],
      'Central Otago': [
        'Pinot noir',
        'Pinot gris',
        'Riesling',
        'Chardonnay',
        'Gewurztraminer',
        'Sauvignon blanc',
      ],
      Gisborne: [
        'Chardonnay',
        'Pinot gris',
        'Gewurztraminer',
        'Sauvignon blanc',
        'Chenin',
        'Albariño',
        'Viognier',
        'Merlot',
      ],
      "Hawke's Bay": [
        ...BORDEAUX_REDS,
        'Syrah',
        'Tempranillo',
        'Chardonnay',
        'Sauvignon blanc',
        'Pinot gris',
        'Viognier',
      ],
      Marlborough: [
        'Sauvignon blanc',
        'Pinot noir',
        'Chardonnay',
        'Pinot gris',
        'Riesling',
        'Gewurztraminer',
        'Grüner Veltliner',
      ],
      Martinborough: [
        'Pinot noir',
        'Syrah',
        'Sauvignon blanc',
        'Chardonnay',
        'Pinot gris',
        'Riesling',
      ],
      Nelson: [
        'Sauvignon blanc',
        'Pinot noir',
        'Chardonnay',
        'Riesling',
        'Pinot gris',
        'Gewurztraminer',
        'Albariño',
      ],
    },
    ZA: {
      Constantia: [
        'Sauvignon blanc',
        'Sémillon',
        'Muscat à petits grains',
        'Chardonnay',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
        'Pinot noir',
      ],
      Elgin: ['Chardonnay', 'Sauvignon blanc', 'Riesling', 'Sémillon', 'Pinot noir', 'Syrah'],
      Franschhoek: [
        'Sémillon',
        'Chenin',
        'Chardonnay',
        'Sauvignon blanc',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
      ],
      Paarl: [
        'Chenin',
        'Chardonnay',
        'Viognier',
        'Roussanne',
        'Syrah',
        'Pinotage',
        'Cabernet-Sauvignon',
        'Merlot',
        'Cinsault',
        'Grenache',
        'Mourvèdre',
      ],
      Stellenbosch: [
        ...BORDEAUX_REDS,
        'Pinotage',
        'Syrah',
        'Cinsault',
        'Chenin',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      Swartland: [
        'Chenin',
        'Grenache blanc',
        'Roussanne',
        'Marsanne',
        'Viognier',
        'Clairette',
        'Sémillon',
        'Palomino',
        'Syrah',
        'Grenache',
        'Mourvèdre',
        'Cinsault',
        'Carignan',
        'Pinotage',
        'Tinta barroca',
      ],
      'Walker Bay': ['Pinot noir', 'Chardonnay', 'Sauvignon blanc', 'Pinotage', 'Syrah'],
    },
    GR: {
      Crète: [
        'Vidiano',
        'Vilana',
        'Assyrtiko',
        'Malvasia',
        'Muscat à petits grains',
        'Liatiko',
        'Kotsifali',
        'Mandilaria',
        'Syrah',
      ],
      Macédoine: [
        'Xinomavro',
        'Negoska',
        'Limnio',
        'Syrah',
        'Cabernet-Sauvignon',
        'Merlot',
        'Malagousia',
        'Assyrtiko',
        'Roditis',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      Naoussa: ['Xinomavro', 'Negoska', 'Merlot', 'Syrah', 'Cabernet-Sauvignon', 'Malagousia'],
      Némée: [
        'Agiorgitiko',
        'Cabernet-Sauvignon',
        'Syrah',
        'Moschofilero',
        'Roditis',
        'Assyrtiko',
        'Malagousia',
      ],
      Santorin: ['Assyrtiko', 'Athiri', 'Aidani', 'Mandilaria', 'Mavrotragano'],
    },
    HU: {
      Eger: [
        'Blaufränkisch',
        'Kadarka',
        'Pinot noir',
        'Cabernet franc',
        'Merlot',
        'Syrah',
        'Welschriesling',
        'Leányka',
        'Chardonnay',
        'Hárslevelű',
        'Furmint',
      ],
      Tokaj: ['Furmint', 'Hárslevelű', 'Muscat à petits grains', 'Kabar', 'Kövérszőlő', 'Zéta'],
      Villány: [
        'Cabernet franc',
        'Cabernet-Sauvignon',
        'Merlot',
        'Blaufränkisch',
        'Portugieser',
        'Kadarka',
        'Pinot noir',
        'Syrah',
        'Welschriesling',
        'Chardonnay',
      ],
    },
    GB: {
      Cornwall: ENGLISH,
      'East Sussex': ENGLISH,
      Essex: ENGLISH,
      Hampshire: ENGLISH,
      Kent: ENGLISH,
      'West Sussex': ENGLISH,
    },
    CA: {
      'British Columbia': BRITISH_COLUMBIA,
      'Niagara Peninsula': [
        'Riesling',
        'Chardonnay',
        'Vidal',
        'Gewurztraminer',
        'Sauvignon blanc',
        'Pinot gris',
        'Cabernet franc',
        'Pinot noir',
        'Gamay',
        'Merlot',
        'Cabernet-Sauvignon',
        'Baco noir',
      ],
      'Nova Scotia': [
        'L’Acadie blanc',
        'Seyval blanc',
        'Vidal',
        'Ortega',
        'Chardonnay',
        'Riesling',
        'Pinot noir',
        'Marquette',
      ],
      'Okanagan Valley': BRITISH_COLUMBIA,
      'Prince Edward County': [
        'Pinot noir',
        'Chardonnay',
        'Riesling',
        'Pinot gris',
        'Gamay',
        'Cabernet franc',
        'Vidal',
      ],
    },
    GE: {
      Imereti: ['Tsolikouri', 'Tsitska', 'Krakhuna', 'Otskhanuri Sapere', 'Aladasturi'],
      Kakheti: ['Saperavi', 'Rkatsiteli', 'Mtsvane', 'Kisi', 'Khikhvi', 'Cabernet-Sauvignon'],
      Kartli: ['Chinuri', 'Goruli Mtsvane', 'Tavkveri', 'Shavkapito', 'Saperavi', 'Chardonnay'],
      'Racha-Lechkhumi': ['Aleksandrouli', 'Mujuretuli', 'Tsolikouri'],
    },
    MA: { Benslimane: MOROCCO, Guerrouane: MOROCCO, Meknès: MOROCCO, Zenata: MOROCCO },
    LB: { Batroun: LEBANON, 'Mont-Liban': LEBANON, 'Vallée de la Bekaa': LEBANON },
    IL: { Galilée: ISRAEL, Golan: ISRAEL, Judée: ISRAEL, Samson: ISRAEL, Shomron: ISRAEL },
    HR: {
      Dalmatie: ['Plavac mali', 'Babić', 'Pošip', 'Grk', 'Debit', 'Maraština', 'Zinfandel'],
      Istrie: ['Malvasia', 'Teran', 'Refosco', 'Muscat à petits grains', 'Merlot', 'Chardonnay'],
      Slavonie: [
        'Welschriesling',
        'Gewurztraminer',
        'Chardonnay',
        'Pinot gris',
        'Riesling',
        'Sauvignon blanc',
        'Blaufränkisch',
        'Pinot noir',
      ],
    },
    SI: {
      'Goriška Brda': [
        'Ribolla gialla',
        'Friulano',
        'Pinot gris',
        'Chardonnay',
        'Sauvignon blanc',
        'Malvasia',
        'Merlot',
        'Cabernet franc',
        'Pinot noir',
      ],
      Podravje: [
        'Welschriesling',
        'Furmint',
        'Sauvignon blanc',
        'Riesling',
        'Pinot gris',
        'Chardonnay',
        'Gewurztraminer',
        'Muscat à petits grains',
        'Pinot noir',
        'Blaufränkisch',
      ],
      Posavje: [
        'Žametovka',
        'Blaufränkisch',
        'Welschriesling',
        'Chardonnay',
        'Sauvignon blanc',
        'Pinot noir',
      ],
      Primorska: [
        'Refosco',
        'Malvasia',
        'Ribolla gialla',
        'Merlot',
        'Cabernet-Sauvignon',
        'Chardonnay',
        'Pinot gris',
        'Muscat à petits grains',
      ],
      Vipava: [
        'Zelen',
        'Pinela',
        'Ribolla gialla',
        'Malvasia',
        'Sauvignon blanc',
        'Chardonnay',
        'Pinot gris',
        'Merlot',
        'Cabernet franc',
        'Barbera',
      ],
    },
    RO: {
      Banat: [
        'Kadarka',
        'Feteasca neagra',
        'Cabernet-Sauvignon',
        'Merlot',
        'Pinot noir',
        'Welschriesling',
        'Feteasca regala',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      Crişana: [
        'Kadarka',
        'Feteasca neagra',
        'Cabernet-Sauvignon',
        'Merlot',
        'Pinot noir',
        'Feteasca regala',
        'Muscat ottonel',
        'Sauvignon blanc',
      ],
      'Dealu Mare': [
        'Feteasca neagra',
        'Cabernet-Sauvignon',
        'Merlot',
        'Pinot noir',
        'Syrah',
        'Tamaioasa romaneasca',
        'Feteasca alba',
        'Feteasca regala',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      Moldova: [
        'Feteasca neagra',
        'Babeasca neagra',
        'Cabernet-Sauvignon',
        'Merlot',
        'Grasa de Cotnari',
        'Tamaioasa romaneasca',
        'Feteasca alba',
        'Feteasca regala',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      Murfatlar: [
        'Feteasca neagra',
        'Cabernet-Sauvignon',
        'Merlot',
        'Pinot noir',
        'Syrah',
        'Chardonnay',
        'Pinot gris',
        'Muscat ottonel',
        'Sauvignon blanc',
      ],
      Transilvania: [
        'Feteasca regala',
        'Feteasca alba',
        'Welschriesling',
        'Riesling',
        'Sauvignon blanc',
        'Muscat ottonel',
        'Gewurztraminer',
        'Pinot gris',
        'Pinot noir',
      ],
    },
    BG: {
      'Danube Plain': [
        'Kadarka',
        'Cabernet-Sauvignon',
        'Merlot',
        'Pinot noir',
        'Syrah',
        'Chardonnay',
        'Sauvignon blanc',
        'Muscat ottonel',
        'Riesling',
      ],
      'Black Sea': [
        'Dimyat',
        'Muscat ottonel',
        'Chardonnay',
        'Sauvignon blanc',
        'Gewurztraminer',
        'Rkatsiteli',
        'Riesling',
        'Cabernet-Sauvignon',
        'Merlot',
      ],
      'Struma Valley': [
        'Melnik',
        'Mavrud',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
        'Sauvignon blanc',
        'Chardonnay',
      ],
      'Thracian Valley': [
        'Mavrud',
        'Rubin',
        'Pamid',
        'Cabernet-Sauvignon',
        'Merlot',
        'Syrah',
        'Cabernet franc',
        'Chardonnay',
        'Sauvignon blanc',
        'Viognier',
      ],
    },
    MD: { Codru: MOLDOVA, 'Ştefan Vodă': MOLDOVA, 'Valul lui Traian': MOLDOVA },
    JP: {
      Hokkaido: ['Kerner', 'Zweigelt', 'Pinot noir', 'Müller-Thurgau', 'Chardonnay'],
      Nagano: ['Merlot', 'Chardonnay', 'Cabernet-Sauvignon', 'Sauvignon blanc', 'Pinot noir'],
      Yamagata: ['Delaware', 'Muscat Bailey A', 'Merlot', 'Chardonnay', 'Cabernet-Sauvignon'],
      Yamanashi: ['Koshu', 'Muscat Bailey A', 'Merlot', 'Cabernet-Sauvignon', 'Chardonnay'],
    },
    CN: { Hebei: CHINA, Ningxia: CHINA, Shandong: CHINA, Xinjiang: CHINA, Yunnan: CHINA },
    UY: { Canelones: URUGUAY, Colonia: URUGUAY, Maldonado: URUGUAY, Montevideo: URUGUAY },
    BR: {
      Campanha: [
        'Tannat',
        'Cabernet-Sauvignon',
        'Merlot',
        'Cabernet franc',
        'Tempranillo',
        'Touriga nacional',
        'Alicante Bouschet',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      'Serra Gaúcha': [
        'Merlot',
        'Cabernet-Sauvignon',
        'Cabernet franc',
        'Pinot noir',
        'Chardonnay',
        'Welschriesling',
        'Muscat à petits grains',
        'Glera',
      ],
      'Serra do Sudeste': [
        'Cabernet-Sauvignon',
        'Merlot',
        'Tannat',
        'Pinot noir',
        'Chardonnay',
        'Sauvignon blanc',
      ],
      'Vale do São Francisco': [
        'Syrah',
        'Cabernet-Sauvignon',
        'Tempranillo',
        'Touriga nacional',
        'Alicante Bouschet',
        'Chenin',
        'Muscat à petits grains',
      ],
    },
    MX: { Coahuila: MEXICO, Querétaro: MEXICO, 'Valle de Guadalupe': MEXICO, Zacatecas: MEXICO },
    LU: {
      'Moselle luxembourgeoise': [
        'Auxerrois',
        'Pinot gris',
        'Pinot blanc',
        'Riesling',
        'Müller-Thurgau',
        'Elbling',
        'Gewurztraminer',
        'Chardonnay',
        'Pinot noir',
      ],
    },
    BE: {
      'Côtes de Sambre et Meuse': LOW_COUNTRIES,
      Hageland: LOW_COUNTRIES,
      Haspengouw: LOW_COUNTRIES,
      Heuvelland: LOW_COUNTRIES,
    },
    NL: { Gelderland: LOW_COUNTRIES, Limburg: LOW_COUNTRIES, Zeeland: LOW_COUNTRIES },
  };

/**
 * The varieties the Cépage list shows for a wine's country and region: the region's; for "Autre
 * région" or no region yet, every variety of the country's regions; undefined (no filter) without
 * a country, or for a country with no list.
 */
export function grapesForPlace(country: string, region: string): string[] | undefined {
  const regions = REGION_GRAPES[country];
  if (!regions) return undefined;
  const own = region !== '' && region !== REGION_OTHER ? regions[region] : undefined;
  if (own) return [...own];
  return [...new Set(Object.values(regions).flat())];
}
